import { Head, useForm, usePage } from "@inertiajs/react"
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    CssBaseline,
    Divider,
    Grid,
    IconButton,
    Paper,
    TextField,
    ThemeProvider,
    Tooltip,
    Typography,
} from "@mui/material"
import CloudIcon from "@mui/icons-material/Cloud"
import LockPersonIcon from "@mui/icons-material/LockPerson"
import DarkModeIcon from "@mui/icons-material/DarkMode"
import LightModeIcon from "@mui/icons-material/LightMode"
import Logo from "../../images/logo-transparent.webp"
import { buildTheme } from "../../lib/theme"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import i18n from "../../lib/i18n"

type Props = {
    errors?: { base?: string }
    theme: "light" | "dark"
    locale: "en" | "es"
}

const LoginPage = () => {
    const { errors, theme: initialTheme, locale: initialLocale } = usePage<Props>().props
    const [themeMode, setThemeMode] = useState<"light" | "dark">(initialTheme)
    const [locale, setLocale] = useState<"en" | "es">(initialLocale)
    const [autofilled, setAutofilled] = useState(false)
    const { data, setData, post, processing } = useForm({ email: "", password: "" })
    const { t } = useTranslation()

    useEffect(() => {
        if (i18n.language !== initialLocale) {
            i18n.changeLanguage(initialLocale)
        }
    }, [initialLocale])

    const handleAutofill = (e: React.AnimationEvent) => {
        if (e.animationName === "mui-auto-fill") setAutofilled(true)
        if (e.animationName === "mui-auto-fill-cancel") setAutofilled(false)
    }

    const handleToggleTheme = () => {
        const next = themeMode === "light" ? "dark" : "light"
        setThemeMode(next)
        fetch("/theme", {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "" },
            body: JSON.stringify({ theme: next }),
        })
    }

    const handleToggleLocale = useCallback(() => {
        const next: "en" | "es" = locale === "en" ? "es" : "en"
        setLocale(next)
        i18n.changeLanguage(next)
        fetch("/locale", {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "" },
            body: JSON.stringify({ locale: next }),
        })
    }, [locale])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        post("/login")
    }

    return (
        <ThemeProvider theme={buildTheme(themeMode)}>
            <CssBaseline />
            <Head title={t("auth.login")} />
            <Box sx={{ position: "fixed", top: 16, right: 16, display: "flex", gap: 1 }}>
                <Tooltip title={locale === "en" ? t("nav.switchToSpanish") : t("nav.switchToEnglish")}>
                    <Button onClick={handleToggleLocale} size="small" sx={{ minWidth: 0, px: 1, fontWeight: "bold", fontSize: "0.75rem" }} color="inherit">
                        {locale === "en" ? "ES" : "EN"}
                    </Button>
                </Tooltip>
                <Tooltip title={themeMode === "dark" ? t("nav.lightMode") : t("nav.darkMode")}>
                    <IconButton onClick={handleToggleTheme}>
                        {themeMode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                <Box sx={{ width: "100%", maxWidth: 900 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                        <img src={Logo} alt="Logo" style={{ height: 64 }} />
                    </Box>

                    <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: "bold" }}>
                        Full Packing del Caribe | ADM
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
                        {t("auth.selectSignIn")}
                    </Typography>

                    {errors?.base && (
                        <Alert severity="error" sx={{ mb: 3 }}>{errors.base}</Alert>
                    )}

                    <Grid container spacing={3}>
                        {/* ADM Cloud */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper variant="outlined" sx={{ p: 4, height: "100%" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                                    <CloudIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>{t("auth.admCloud")}</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {t("auth.admCloudDesc")}
                                </Typography>
                                <Box component="form" onSubmit={handleSubmit}>
                                    <TextField
                                        label={t("auth.email")}
                                        type="email"
                                        fullWidth
                                        required
                                        margin="normal"
                                        autoComplete="email"
                                        autoFocus
                                        disabled={processing}
                                        value={data.email}
                                        onChange={e => setData("email", e.target.value)}
                                        slotProps={{
                                            inputLabel: { shrink: !!data.email || autofilled },
                                            htmlInput: { onAnimationStart: handleAutofill },
                                        }}
                                    />
                                    <TextField
                                        label={t("auth.password")}
                                        type="password"
                                        fullWidth
                                        required
                                        margin="normal"
                                        autoComplete="current-password"
                                        disabled={processing}
                                        value={data.password}
                                        onChange={e => setData("password", e.target.value)}
                                        slotProps={{
                                            inputLabel: { shrink: !!data.password || autofilled },
                                            htmlInput: { onAnimationStart: handleAutofill },
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        sx={{ mt: 3 }}
                                        disabled={processing}
                                        startIcon={processing ? <CircularProgress size={18} color="inherit" /> : null}
                                    >
                                        {processing ? t("auth.signingIn") : t("auth.signIn")}
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Divider vertical */}
                        <Grid size={{ xs: 12, md: "auto" }} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
                            <Divider sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>{t("auth.or")}</Divider>
                        </Grid>

                        {/* System (future) */}
                        <Grid size={{ xs: 12, md: "grow" }}>
                            <Paper
                                variant="outlined"
                                sx={{ p: 4, height: "100%", opacity: 0.5, pointerEvents: "none", userSelect: "none" }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                                    <LockPersonIcon />
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>{t("auth.system")}</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {t("auth.systemDesc")}
                                </Typography>
                                <TextField label={t("auth.email")} type="email" fullWidth margin="normal" disabled />
                                <TextField label={t("auth.password")} type="password" fullWidth margin="normal" disabled />
                                <Button fullWidth variant="outlined" size="large" sx={{ mt: 3 }} disabled>
                                    {t("auth.comingSoon")}
                                </Button>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </ThemeProvider>
    )
}

LoginPage.layout = (page: React.ReactNode) => <>{page}</>

export default LoginPage
